import { Stack } from '@/components/layout';
import { Button, TextInput } from '@/components/ui';
import { Select } from '@/components/ui/select';
import Text from '@/components/ui/text/text';
import { STRINGS } from '@/constants/strings';
import { AuthView } from '@/features/auth/components';
import { useEnterEmail } from '@/features/auth/hooks/use-enter-email';
import { createStyles, useScaling } from '@/hooks';
import { envPublic } from '@/libs/config';
import type { FirebaseEnv } from '@/libs/firebase/init';
import { useFirebaseEnvStore } from '@/stores';
import { colors } from '@/theme/colors';
import { useMemo } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useShallow } from 'zustand/shallow';

export function EnterEmailScreen() {
  const { email, onChangeEmail, onBlurEmail, handleSubmit } = useEnterEmail();
  const { scale } = useScaling();
  const styles = useStyles();
  const env = useFirebaseEnvStore(useShallow(s => s.env));
  const { bottom } = useSafeAreaInsets();
  const setEnv = useFirebaseEnvStore(useShallow(s => s.setEnv));
  const environmentOptions = useMemo(
    () => [
      { value: 'development', label: STRINGS.auth.enterEmail.environmentDevelopment },
      { value: 'production', label: STRINGS.auth.enterEmail.environmentProduction },
    ],
    [],
  );
  return (
    <>
      <AuthView>
        <AuthView.Content>
          <Stack
            paddingTop={scale({
              phone: 120,
              tablet: 60,
            })}
            align="center">
            <AuthView.PlayerPathwayImage />
          </Stack>

          <Stack
            width="100%"
            paddingX={scale({
              phone: 16,
              tablet: 40,
            })}
            paddingTop={scale(48)}
            gap={scale(24)}
            align="center">
            <TextInput value={email} onChangeText={onChangeEmail} status={'default'}>
              <TextInput.Content>
                <TextInput.Icon
                  size={scale({
                    phone: 16,
                    tablet: 20,
                  })}
                  name="IcEnvelope"
                  position="left"
                />
                <TextInput.Field
                  placeholder={STRINGS.auth.enterEmail.placeholderEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoComplete="username"
                  textContentType="username"
                  importantForAutofill="yesExcludeDescendants"
                  maxLength={100}
                  autoCorrect={true}
                  placeholderTextColor={colors['Labels/Tertiary']}
                  onBlur={onBlurEmail}
                />
              </TextInput.Content>
              {/* <TextInput.HelperText>{errors.email?.message ?? ' '}</TextInput.HelperText> */}
            </TextInput>

            <Button onPress={handleSubmit} style={{ width: scale(212) }}>
              <Button.Text>{STRINGS.auth.enterEmail.buttonNext}</Button.Text>
            </Button>
          </Stack>

          <Stack flex={1} />

          <Stack align="center" gap={scale(4)}>
            <AuthView.Hint />
            <AuthView.Logo />
          </Stack>
        </AuthView.Content>
      </AuthView>
      {(envPublic.environment === 'development' || envPublic.environment === 'staging') && (
        <Stack
          align="center"
          justify="center"
          position="absolute"
          bottom={bottom + (bottom === 0 ? 4 : 0)}
          gap={scale(4)}
          direction="row"
          width="100%">
          <Text variant="Font Inter/Subtitle/Regular 12px" style={styles.selectTriggerText}>
            Environment:
          </Text>
          <Select
            options={environmentOptions}
            value={env}
            onValueChange={value => setEnv(value as FirebaseEnv)}
            placeholder={STRINGS.auth.enterEmail.environmentPlaceholder}>
            <Select.Trigger
              style={styles.selectTrigger}
              textColor={colors['0']}
              labelVariant="Font Inter/Subtitle/Regular 11px"
              hideIcon={false}
            />
            <Select.Content maxHeight={scale(200)} />
          </Select>
        </Stack>
      )}
    </>
  );
}

const useStyles = createStyles(({ scale }) => ({
  selectTrigger: {
    width: scale(100),
    height: scale(32),
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderWidth: 0,
  },
  selectTriggerText: {
    color: colors['0'],
  },
}));
